import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, getSessionId, euro, WHATSAPP_NUMBERS } from "../lib/api";
import { track } from "../lib/track";
import { toast } from "sonner";
import {
  X, ArrowLeft, Check, CreditCard, Building2, FileText, Video, CalendarClock,
  Lock, ShieldCheck, Loader2, MessageCircle,
} from "lucide-react";

const spring = { type: "spring", stiffness: 300, damping: 30 };
const variants = {
  enter: { x: 40, opacity: 0 },
  center: { x: 0, opacity: 1 },
  exit: { x: -40, opacity: 0 },
};

function BigButton({ children, onClick, active, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full h-16 md:h-[68px] rounded-2xl border text-lg text-left px-6 flex items-center justify-between transition-all active:scale-[0.98]
        ${active ? "bg-ink text-white border-ink" : "bg-white border-border hover:border-ink/40 hover:-translate-y-0.5"}`}
    >
      {children}
    </button>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-ink-soft">{label}</span>
      <input
        {...props}
        className="mt-1.5 w-full h-12 px-4 rounded-xl border border-border bg-white focus:border-ink outline-none transition-colors"
      />
    </label>
  );
}

export function Wizard({ vehicle, config, onClose, onBlocked }) {
  const [lead, setLead] = useState(null);
  const [step, setStep] = useState("permuta_q");
  const [history, setHistory] = useState([]);
  const [permuta, setPermuta] = useState({ has_permuta: false });
  const [purchaseMode, setPurchaseMode] = useState(null);
  const [financing, setFinancing] = useState({ down_payment: "", duration: "60", insurance: false });
  const [customer, setCustomer] = useState({ name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [blockMethod, setBlockMethod] = useState(null);
  const created = useRef(false);

  useEffect(() => {
    if (created.current) return;
    created.current = true;
    api.post("/leads", { vehicle_id: vehicle.id, session_id: getSessionId() })
      .then((r) => setLead(r.data));
  }, [vehicle.id]);

  const monthly = (() => {
    const dp = Number(financing.down_payment) || 0;
    const dur = Number(financing.duration) || 60;
    const principal = Math.max(vehicle.price - dp, 0);
    const rate = 0.0069; // ~8.3% annuo indicativo
    const m = principal * (rate * Math.pow(1 + rate, dur)) / (Math.pow(1 + rate, dur) - 1);
    return Math.round(m + (financing.insurance ? 22 : 0));
  })();

  const go = (next, patch) => {
    setHistory((h) => [...h, step]);
    if (patch && lead) api.patch(`/leads/${lead.id}`, { phase: next, ...patch }).catch(() => {});
    setStep(next);
  };
  const back = () => {
    setHistory((h) => {
      if (!h.length) return h;
      setStep(h[h.length - 1]);
      return h.slice(0, -1);
    });
  };

  const finalize = async (type) => {
    setSubmitting(true);
    try {
      await api.patch(`/leads/${lead.id}`, {
        customer, purchase_mode: purchaseMode,
        financing: purchaseMode === "Finanziamento" ? { ...financing, monthly } : null,
        permuta,
      });
      await api.post(`/leads/${lead.id}/action`, { action_type: type });
      const labels = { quote: "Preventivo richiesto!", videocall: "Videochiamata richiesta!", appointment: "Appuntamento richiesto!" };
      toast.success(labels[type] + " Ti ricontatteremo a breve.");
      setStep("done");
    } catch (e) {
      toast.error("Errore, riprova.");
    } finally { setSubmitting(false); }
  };

  const saveProgress = async () => {
    if (!lead) return;
    await api.patch(`/leads/${lead.id}`, { customer, purchase_mode: purchaseMode, permuta,
      financing: purchaseMode === "Finanziamento" ? { ...financing, monthly } : null });
    toast.success(`Preventivo salvato. Riprendilo con il codice: ${lead.resume_token}`, { duration: 8000 });
  };

  const startBlock = async (method) => {
    setSubmitting(true);
    try {
      await api.patch(`/leads/${lead.id}`, { customer, purchase_mode: purchaseMode, permuta });
      if (method === "card") {
        const { data } = await api.post("/payments/checkout", { lead_id: lead.id, origin_url: window.location.origin });
        window.location.href = data.url;
        return;
      }
      await api.post(`/leads/${lead.id}/block`, { method: "bonifico" });
      toast.success("Vettura bloccata! Segui le istruzioni per il bonifico.");
      setStep("block_bonifico");
      if (onBlocked) onBlocked();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile bloccare la vettura.");
    } finally { setSubmitting(false); }
  };

  const waLink = (number) => {
    const txt = encodeURIComponent(
      `Ciao Fantini Next Mobility! Sono interessato a ${vehicle.make} ${vehicle.model} (${euro(vehicle.price)}). ` +
      `Modalità: ${purchaseMode || "-"}. ${customer.name ? "Sono " + customer.name + "." : ""}`
    );
    return `https://wa.me/${number}?text=${txt}`;
  };

  const custValid = customer.name && customer.phone && customer.email;

  const steps = [
    "permuta_q", permuta.has_permuta && "permuta_form", "purchase_q",
    purchaseMode === "Finanziamento" && "financing", "customer", "summary",
  ].filter(Boolean);
  const progress = Math.min((steps.indexOf(step) + 1) / steps.length, 1);

  return (
    <motion.div
      data-testid="wizard-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background overflow-y-auto"
    >
      {/* top bar */}
      <div className="sticky top-0 glass border-b border-border z-10">
        <div className="max-w-xl mx-auto px-5 h-16 flex items-center justify-between">
          <button data-testid="wizard-back" onClick={history.length ? back : onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors">
            <ArrowLeft size={18} strokeWidth={1.5} />
          </button>
          <div className="text-xs uppercase tracking-widest text-ink-soft">{vehicle.make} {vehicle.model}</div>
          <button data-testid="wizard-close" onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
        {step !== "done" && step !== "block_bonifico" && (
          <div className="h-0.5 bg-secondary">
            <motion.div className="h-full bg-ink" animate={{ width: `${progress * 100}%` }} transition={spring} />
          </div>
        )}
      </div>

      <div className="max-w-xl mx-auto px-5 py-10 pb-32">
        <AnimatePresence mode="wait">
          <motion.div key={step} variants={variants} initial="enter" animate="center" exit="exit" transition={spring}>

            {step === "permuta_q" && (
              <div>
                <StepTitle n="01" q={config.permuta_question} />
                <div className="flex flex-col gap-4 mt-8">
                  <BigButton testid="permuta-yes" onClick={() => { setPermuta({ has_permuta: true }); go("permuta_form", { permuta: { has_permuta: true } }); }}>
                    Sì, ho un'auto da permutare <Check size={20} className="text-sand" />
                  </BigButton>
                  <BigButton testid="permuta-no" onClick={() => { setPermuta({ has_permuta: false }); go("purchase_q", { permuta: { has_permuta: false } }); }}>
                    No, grazie
                  </BigButton>
                </div>
              </div>
            )}

            {step === "permuta_form" && (
              <div>
                <StepTitle n="01" q="Raccontaci la tua auto in permuta" />
                <div className="grid grid-cols-2 gap-4 mt-8">
                  <Field label="Marca" data-testid="permuta-make" value={permuta.make || ""} onChange={(e) => setPermuta({ ...permuta, make: e.target.value })} />
                  <Field label="Modello" data-testid="permuta-model" value={permuta.model || ""} onChange={(e) => setPermuta({ ...permuta, model: e.target.value })} />
                  <Field label="Targa" data-testid="permuta-plate" value={permuta.plate || ""} onChange={(e) => setPermuta({ ...permuta, plate: e.target.value })} />
                  <Field label="Anno" type="number" data-testid="permuta-year" value={permuta.year || ""} onChange={(e) => setPermuta({ ...permuta, year: e.target.value })} />
                  <Field label="Chilometri" type="number" data-testid="permuta-km" value={permuta.km || ""} onChange={(e) => setPermuta({ ...permuta, km: e.target.value })} />
                  <Field label="Versione" data-testid="permuta-version" value={permuta.version || ""} onChange={(e) => setPermuta({ ...permuta, version: e.target.value })} />
                  <Field label="Finanziamento residuo (€)" type="number" data-testid="permuta-residual" value={permuta.residual || ""} onChange={(e) => setPermuta({ ...permuta, residual: e.target.value })} />
                  <label className="block col-span-1">
                    <span className="text-xs uppercase tracking-wider text-ink-soft">Condizioni</span>
                    <select data-testid="permuta-condition" value={permuta.condition || "Buone"} onChange={(e) => setPermuta({ ...permuta, condition: e.target.value })}
                      className="mt-1.5 w-full h-12 px-4 rounded-xl border border-border bg-white outline-none">
                      <option>Ottime</option><option>Buone</option><option>Discrete</option><option>Da valutare</option>
                    </select>
                  </label>
                </div>
                <FileHint label="Fotografie dell'auto" testid="permuta-photos" onChange={(files) => setPermuta((p) => ({ ...p, photos: files }))} />
                <FileHint label="Carta di circolazione" testid="permuta-libretto" single onChange={(files) => setPermuta((p) => ({ ...p, libretto: files }))} />
                <PrimaryBtn testid="permuta-continue" onClick={() => go("purchase_q", { permuta })}>Continua</PrimaryBtn>
              </div>
            )}

            {step === "purchase_q" && (
              <div>
                <StepTitle n="02" q={config.purchase_question} />
                <div className="flex flex-col gap-4 mt-8">
                  <BigButton testid="mode-direct" active={purchaseMode === "Acquisto diretto"}
                    onClick={() => { setPurchaseMode("Acquisto diretto"); go("customer", { purchase_mode: "Acquisto diretto" }); }}>
                    Acquisto diretto <CreditCard size={20} className="opacity-60" />
                  </BigButton>
                  <BigButton testid="mode-financing" active={purchaseMode === "Finanziamento"}
                    onClick={() => { setPurchaseMode("Finanziamento"); go("financing", { purchase_mode: "Finanziamento" }); }}>
                    Finanziamento <FileText size={20} className="opacity-60" />
                  </BigButton>
                  {vehicle.leasing_available && (
                    <BigButton testid="mode-leasing" active={purchaseMode === "Leasing"}
                      onClick={() => { setPurchaseMode("Leasing"); go("customer", { purchase_mode: "Leasing" }); }}>
                      Leasing <Building2 size={20} className="opacity-60" />
                    </BigButton>
                  )}
                </div>
              </div>
            )}

            {step === "financing" && (
              <div>
                <StepTitle n="03" q={config.financing_intro} />
                <div className="mt-8 space-y-6">
                  <Field label="Anticipo (€)" type="number" data-testid="fin-down" value={financing.down_payment}
                    onChange={(e) => setFinancing({ ...financing, down_payment: e.target.value })} placeholder="es. 5000" />
                  <div>
                    <span className="text-xs uppercase tracking-wider text-ink-soft">Durata: {financing.duration} mesi</span>
                    <input type="range" min="12" max="84" step="12" data-testid="fin-duration" value={financing.duration}
                      onChange={(e) => setFinancing({ ...financing, duration: e.target.value })}
                      className="w-full mt-3 accent-ink" />
                    <div className="flex justify-between text-[10px] text-ink-soft mt-1"><span>12</span><span>84</span></div>
                  </div>
                  <label className="flex items-center gap-3 p-4 rounded-xl border border-border bg-white cursor-pointer">
                    <input type="checkbox" data-testid="fin-insurance" checked={financing.insurance}
                      onChange={(e) => setFinancing({ ...financing, insurance: e.target.checked })} className="w-5 h-5 accent-ink" />
                    <span className="text-sm">Aggiungi servizi assicurativi (+€22/mese)</span>
                  </label>
                  <div className="p-6 rounded-2xl bg-ink text-white">
                    <div className="text-xs uppercase tracking-widest text-white/50">Rata mensile indicativa</div>
                    <div className="font-serif text-4xl mt-1" data-testid="fin-monthly">{euro(monthly)}<span className="text-lg text-white/50">/mese</span></div>
                    <div className="text-[11px] text-white/40 mt-2">Importo da finanziare: {euro(Math.max(vehicle.price - (Number(financing.down_payment) || 0), 0))}. Valore indicativo, salvo approvazione.</div>
                  </div>
                </div>
                <PrimaryBtn testid="fin-continue" onClick={() => go("customer", { financing: { ...financing, monthly } })}>Continua</PrimaryBtn>
              </div>
            )}

            {step === "customer" && (
              <div>
                <StepTitle n="04" q="Come possiamo ricontattarti?" />
                <div className="mt-8 space-y-4">
                  <Field label="Nome e cognome" data-testid="cust-name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
                  <Field label="Telefono" data-testid="cust-phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
                  <Field label="Email" type="email" data-testid="cust-email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} />
                </div>
                <PrimaryBtn testid="cust-continue" disabled={!custValid} onClick={() => go("summary", { customer })}>Vai al riepilogo</PrimaryBtn>
                <button onClick={saveProgress} data-testid="save-progress" className="w-full text-center text-sm text-ink-soft mt-4 underline underline-offset-4">Salva e riprendi più tardi</button>
              </div>
            )}

            {step === "summary" && (
              <div>
                <StepTitle n="05" q="Ecco il tuo riepilogo" />
                <div className="mt-8 bg-white rounded-3xl overflow-hidden shadow-ambient">
                  <img src={vehicle.images?.[0] || "/placeholder-car.jpg"} onError={(e) => { e.currentTarget.src = "/placeholder-car.jpg"; }} alt="" className="w-full h-40 object-cover" />
                  <div className="p-6 space-y-3 text-sm">
                    <Row k="Vettura" v={`${vehicle.make} ${vehicle.model} ${vehicle.version}`} />
                    <Row k="Prezzo" v={euro(vehicle.price)} />
                    {permuta.has_permuta && <Row k="Permuta" v={`${permuta.make || ""} ${permuta.model || ""} ${permuta.year || ""}`} />}
                    <Row k="Modalità" v={purchaseMode} />
                    {purchaseMode === "Finanziamento" && <>
                      <Row k="Anticipo" v={euro(financing.down_payment || 0)} />
                      <Row k="Importo da finanziare" v={euro(Math.max(vehicle.price - (Number(financing.down_payment) || 0), 0))} />
                      <Row k="Rata indicativa" v={`${euro(monthly)}/mese × ${financing.duration}m`} />
                    </>}
                    <Row k="Cliente" v={`${customer.name} · ${customer.phone}`} last />
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-3">
                  <button data-testid="cta-block" onClick={() => setStep("block")}
                    className="w-full h-[68px] rounded-2xl bg-ink text-white text-lg flex items-center justify-center gap-3 hover:opacity-90 transition-opacity active:scale-[0.98] shadow-floating">
                    <Lock size={20} strokeWidth={1.5} /> Blocca questa vettura
                  </button>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-1">
                    <SecBtn testid="cta-quote" icon={FileText} onClick={() => finalize("quote")} busy={submitting}>Preventivo completo</SecBtn>
                    <SecBtn testid="cta-videocall" icon={Video} onClick={() => finalize("videocall")} busy={submitting}>Videochiamata</SecBtn>
                    <SecBtn testid="cta-appointment" icon={CalendarClock} onClick={() => finalize("appointment")} busy={submitting}>Appuntamento</SecBtn>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-1">
                    {WHATSAPP_NUMBERS.map((w, i) => (
                      <a key={i} href={waLink(w.number)} target="_blank" rel="noreferrer" data-testid={`cta-whatsapp-${i}`} onClick={() => track("click_whatsapp", { label: "wizard", vehicle_id: vehicle.id })}
                        className="h-12 rounded-2xl border border-border bg-white flex items-center justify-center gap-2 text-xs hover:border-forest/40 transition-colors">
                        <MessageCircle size={15} strokeWidth={1.5} className="text-forest" /> {w.label}
                      </a>
                    ))}
                  </div>
                  <button onClick={saveProgress} data-testid="save-progress-2" className="text-center text-sm text-ink-soft mt-2 underline underline-offset-4">Salva e riprendi più tardi</button>
                </div>
              </div>
            )}

            {step === "block" && (
              <div>
                <StepTitle n="✦" q="Blocca questa vettura" />
                <div className="mt-4 p-5 rounded-2xl bg-secondary text-sm text-ink-soft leading-relaxed">
                  <ShieldCheck size={18} className="text-forest mb-2" strokeWidth={1.5} />
                  {config.block_conditions}
                </div>
                <div className="mt-4 p-4 rounded-2xl border border-border bg-white flex items-center justify-between">
                  <span className="text-sm text-ink-soft">Deposito richiesto</span>
                  <span className="font-serif text-2xl">{euro(vehicle.deposit_amount)}</span>
                </div>
                <div className="flex flex-col gap-4 mt-6">
                  <BigButton testid="block-card" active={blockMethod === "card"} onClick={() => setBlockMethod("card")}>
                    Carta di credito <CreditCard size={20} className="opacity-60" />
                  </BigButton>
                  <BigButton testid="block-bonifico" active={blockMethod === "bonifico"} onClick={() => setBlockMethod("bonifico")}>
                    Bonifico immediato <Building2 size={20} className="opacity-60" />
                  </BigButton>
                </div>
                <PrimaryBtn testid="block-confirm" disabled={!blockMethod || submitting}
                  onClick={() => startBlock(blockMethod)}>
                  {submitting ? <Loader2 className="animate-spin" size={18} /> : `Blocca ora · ${euro(vehicle.deposit_amount)}`}
                </PrimaryBtn>
              </div>
            )}

            {step === "block_bonifico" && (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-forest/10 text-forest flex items-center justify-center mx-auto"><Lock size={28} strokeWidth={1.5} /></div>
                <h2 className="font-serif text-3xl mt-5">Vettura bloccata!</h2>
                <p className="text-ink-soft mt-2 text-sm">Effettua il bonifico entro 30 minuti per confermare.</p>
                <div className="mt-6 p-6 rounded-2xl bg-white shadow-ambient text-left text-sm space-y-2">
                  <Row k="Intestatario" v="Fantini Auto Srl" />
                  <Row k="IBAN" v="IT60 X054 2811 1010 0000 0123 456" />
                  <Row k="Causale" v={`Deposito ${vehicle.make} ${vehicle.model}`} />
                  <Row k="Importo" v={euro(vehicle.deposit_amount)} last />
                </div>
                <PrimaryBtn testid="bonifico-done" onClick={onClose}>Ho capito</PrimaryBtn>
              </div>
            )}

            {step === "done" && (
              <div className="text-center py-10">
                <div className="w-16 h-16 rounded-full bg-forest/10 text-forest flex items-center justify-center mx-auto"><Check size={30} strokeWidth={1.5} /></div>
                <h2 className="font-serif text-3xl mt-5">Richiesta inviata!</h2>
                <p className="text-ink-soft mt-2 text-sm">Ti abbiamo inviato il riepilogo via email. Un consulente Fantini ti ricontatterà a breve.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
                  {WHATSAPP_NUMBERS.map((w, i) => (
                    <a key={i} href={waLink(w.number)} target="_blank" rel="noreferrer" data-testid={`done-whatsapp-${i}`} onClick={() => track("click_whatsapp", { label: "wizard-fine", vehicle_id: vehicle.id })}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border bg-white text-sm hover:border-forest/40 transition-colors">
                      <MessageCircle size={16} className="text-forest" /> {w.label}
                    </a>
                  ))}
                </div>
                <button onClick={onClose} data-testid="done-close" className="block mx-auto mt-4 text-sm text-ink-soft underline underline-offset-4">Chiudi</button>
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

function StepTitle({ n, q }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.3em] text-sand">Passo {n}</div>
      <h2 className="font-serif text-3xl md:text-4xl font-light leading-tight mt-2">{q}</h2>
    </div>
  );
}
function Row({ k, v, last }) {
  return (
    <div className={`flex justify-between gap-4 ${last ? "" : "border-b border-border pb-2"}`}>
      <span className="text-ink-soft">{k}</span>
      <span className="text-ink font-medium text-right">{v}</span>
    </div>
  );
}
function PrimaryBtn({ children, testid, ...props }) {
  return (
    <button data-testid={testid} {...props}
      className="w-full h-14 mt-8 rounded-2xl bg-ink text-white flex items-center justify-center gap-2 hover:opacity-90 transition-opacity active:scale-[0.98] disabled:opacity-40">
      {children}
    </button>
  );
}
function SecBtn({ children, icon: Icon, testid, busy, ...props }) {
  return (
    <button data-testid={testid} disabled={busy} {...props}
      className="h-14 rounded-2xl border border-border bg-white flex flex-col items-center justify-center gap-1 text-xs hover:border-ink/40 hover:-translate-y-0.5 transition-all disabled:opacity-50">
      <Icon size={17} strokeWidth={1.5} className="text-sand" />{children}
    </button>
  );
}
function FileHint({ label, testid, single, onChange }) {
  const [names, setNames] = useState([]);
  return (
    <label className="block mt-4">
      <span className="text-xs uppercase tracking-wider text-ink-soft">{label}</span>
      <div className="mt-1.5 border border-dashed border-border rounded-xl p-4 bg-white text-sm text-ink-soft cursor-pointer hover:border-ink/40 transition-colors">
        <input type="file" accept="image/*,.pdf" multiple={!single} data-testid={testid} className="hidden"
          onChange={(e) => { const f = [...e.target.files].map((x) => x.name); setNames(f); onChange && onChange(f); }} />
        {names.length ? names.join(", ") : "Tocca per caricare"}
      </div>
    </label>
  );
}
